/**
 * Gap detection and book-checksum verification, per subscription.
 *
 * ```
 * on frame:
 *     if frame.seq == last_seq + 1:  apply; last_seq = frame.seq
 *     elif frame.seq <= last_seq:    discard (duplicate or reorder)
 *     else:                          GAP
 * ```
 *
 * On a gap the client **must** stop applying, mark the panel visibly stale,
 * re-fetch the REST snapshot, resubscribe, and resume from the new `ack`
 * (asyncapi §5.5, docs/04 §9.3). Never swallow a gap: a book that has silently
 * dropped a delta shows a price level that does not exist, on a 1¢ tick, on a
 * market that settles in under a minute. Between the gap and the re-snapshot the
 * client renders the **last known good book, visibly marked stale** — never a
 * blank panel, and never a book it knows is wrong.
 *
 * Two detectors, because they catch different bugs:
 *
 * - **`seq`** catches *dropped* frames.
 * - **The checksum** catches *misapplied* ones — an off-by-one in delta
 *   application leaves the sequence perfectly contiguous and the ladder wrong.
 *   That is the bug class that actually ships, and every venue that launched
 *   without a checksum added one later.
 *
 * They run at different moments, and that is deliberate. `seq` is checked **on
 * receipt**, in arrival order, because it decides whether a frame may be
 * buffered at all. The checksum is verified **at flush**, immediately after the
 * ladder is mutated, because it is a statement about the resulting book and the
 * resulting book does not exist until then.
 *
 * `ticker` is exempt. It carries no `seq` by design — every frame is a complete
 * replacement, so a dropped one costs a single stale tick and is repaired 100 ms
 * later. Treating its missing `seq` as a gap would turn a dropped packet into an
 * HTTP request storm at 10 Hz.
 */

import type { ChannelFrame, ChannelName } from './protocol'
import { parseChannel } from './channels'

export type SeqVerdict =
  /** In sequence. Buffer it. */
  | 'APPLY'
  /** `seq <= last_seq`. A duplicate or a reorder; drop it silently. */
  | 'DUPLICATE'
  /** `seq > last_seq + 1`. Stop, re-snapshot, resubscribe. */
  | 'GAP'
  /** `ticker` — no `seq`, no gap semantics, always applied. */
  | 'UNSEQUENCED'
  /** Recovery is already in flight for this channel; the frame belongs to a
   *  stream the client is about to replace. */
  | 'RECOVERING'
  /** A sequenced channel sent a frame with no `seq` at all. Not a gap — a
   *  protocol violation, and worth counting separately from one. */
  | 'MALFORMED'

export type ChannelSyncState = 'PENDING' | 'SYNCED' | 'STALE' | 'RECOVERING'

export type RecoveryReason =
  | 'SEQ_GAP'
  | 'CHECKSUM_MISMATCH'
  | 'BUFFER_LOSS'
  | 'TERMINAL_ERROR'
  /** A recovery was started and never finished — the resubscribe `ack` that
   *  would have re-anchored the channel did not arrive. Retrying is the only
   *  answer that is not "drop every frame forever". */
  | 'RECOVERY_STALLED'

export interface ChannelSequence {
  channel: ChannelName
  state: ChannelSyncState
  /** Highest `seq` applied. `null` before the first frame. */
  lastSeq: number | null
  /** The `seq` the next frame must carry. From the `ack`, then `lastSeq + 1`. */
  expectedSeq: number | null
  gaps: number
  checksumMismatches: number
  duplicates: number
}

export interface RecoveryRequest {
  channel: ChannelName
  reason: RecoveryReason
  /** Last good `seq`, for the log line that makes a recovery diagnosable. */
  lastSeq: number | null
  /** The `seq` that triggered it, for `SEQ_GAP`. */
  observedSeq?: number
}

export interface SequencerHandlers {
  /**
   * A channel needs the recovery procedure run against it. The sequencer detects;
   * the caller executes, because the procedure needs the REST client and the
   * subscription registry and this file needs neither.
   */
  onRecoveryNeeded?: (request: RecoveryRequest) => void
  /** Any transition of a channel's sync state — what the `STALE` pill renders. */
  onStateChange?: (channel: ChannelName, state: ChannelSyncState) => void
}

export class Sequencer {
  readonly #channels = new Map<ChannelName, ChannelSequence>()
  readonly #handlers: SequencerHandlers

  constructor(handlers: SequencerHandlers = {}) {
    this.#handlers = handlers
  }

  get channels(): ChannelSequence[] {
    return [...this.#channels.values()]
  }

  get(channel: ChannelName): ChannelSequence | undefined {
    return this.#channels.get(channel)
  }

  stateOf(channel: ChannelName): ChannelSyncState {
    return this.#channels.get(channel)?.state ?? 'PENDING'
  }

  /** Channels that are not currently delivering a trustworthy stream. The
   *  panels these feed render last-known-good behind a `STALE` pill, and order
   *  entry against them is disabled (docs/03 §9.2.1). */
  get staleChannels(): ChannelName[] {
    return this.channels.filter((c) => c.state === 'STALE' || c.state === 'RECOVERING').map((c) => c.channel)
  }

  /**
   * Anchor a subscription on the `seq` its `ack` promised.
   *
   * The ack carries "the `seq` the first frame will carry — always 1", so the
   * anchor is an *expected* value and not a *last applied* one. Anchoring on
   * `seq − 1` instead would silently accept a stream that started at the wrong
   * place.
   */
  anchor(channel: ChannelName, ackSeq: number): void {
    const existing = this.#channels.get(channel)
    const record: ChannelSequence = {
      channel,
      state: 'SYNCED',
      lastSeq: null,
      expectedSeq: ackSeq,
      gaps: existing?.gaps ?? 0,
      checksumMismatches: existing?.checksumMismatches ?? 0,
      duplicates: existing?.duplicates ?? 0,
    }
    this.#channels.set(channel, record)
    this.#emitState(record, 'SYNCED')
  }

  /**
   * Check one data frame.
   *
   * A `snapshot` re-anchors rather than being sequence-checked: it is the base
   * the following deltas apply to, and its own `seq` is the position that base
   * represents. After it, the next frame must be `seq + 1` — the per-frame
   * increment rule applies to the snapshot frame like any other frame on the
   * subscription (register §F15a).
   */
  check(frame: ChannelFrame): SeqVerdict {
    const channel = frame.channel
    const descriptor = parseChannel(channel)
    if (!descriptor.sequenced) return 'UNSEQUENCED'

    const record = this.#channels.get(channel) ?? this.#track(channel)
    if (record.state === 'RECOVERING') return 'RECOVERING'

    const seq = frame.seq
    if (typeof seq !== 'number') return 'MALFORMED'

    if (frame.type === 'snapshot') {
      // A snapshot is authoritative wherever it lands. It is also the exit from
      // a stale state: the client asked for exactly this to repair itself.
      record.lastSeq = seq
      record.expectedSeq = seq + 1
      this.#setState(record, 'SYNCED')
      return 'APPLY'
    }

    const expected = record.expectedSeq
    if (expected === null || seq === expected) {
      record.lastSeq = seq
      record.expectedSeq = seq + 1
      if (record.state !== 'SYNCED') this.#setState(record, 'SYNCED')
      return 'APPLY'
    }

    if (record.lastSeq !== null && seq <= record.lastSeq) {
      record.duplicates += 1
      return 'DUPLICATE'
    }

    record.gaps += 1
    this.#setState(record, 'STALE')
    this.#handlers.onRecoveryNeeded?.({
      channel,
      reason: 'SEQ_GAP',
      lastSeq: record.lastSeq,
      observedSeq: seq,
    })
    return 'GAP'
  }

  /**
   * Report the result of verifying a book frame's checksum against the ladder it
   * produced.
   *
   * Called from the flush, after the delta has been applied. A mismatch takes
   * exactly the same recovery path as a gap — the ladder is equally wrong either
   * way, and having one repair path rather than two is what makes it testable.
   */
  verifyChecksum(channel: ChannelName, expected: number, computed: number): boolean {
    if (expected === computed) return true
    const record = this.#channels.get(channel) ?? this.#track(channel)
    record.checksumMismatches += 1
    if (record.state === 'RECOVERING') return false
    this.#setState(record, 'STALE')
    this.#handlers.onRecoveryNeeded?.({
      channel,
      reason: 'CHECKSUM_MISMATCH',
      lastSeq: record.lastSeq,
    })
    return false
  }

  /**
   * A buffer dropped a frame under load.
   *
   * `seq` continuity was broken by *this client*, not by the server, and the
   * answer is identical: the book is missing a delta and only a snapshot can say
   * what it should hold. Distinguishing the two would be a distinction the
   * ladder cannot see.
   */
  noteBufferLoss(channel: ChannelName): void {
    const record = this.#channels.get(channel) ?? this.#track(channel)
    if (record.state === 'RECOVERING' || record.state === 'STALE') return
    this.#setState(record, 'STALE')
    this.#handlers.onRecoveryNeeded?.({ channel, reason: 'BUFFER_LOSS', lastSeq: record.lastSeq })
  }

  /** A terminal `error` frame took the subscription away. Same recovery, one
   *  extra step: the channel has to be resubscribed as well as re-snapshotted. */
  noteTerminalError(channel: ChannelName): void {
    const record = this.#channels.get(channel) ?? this.#track(channel)
    this.#setState(record, 'STALE')
    this.#handlers.onRecoveryNeeded?.({ channel, reason: 'TERMINAL_ERROR', lastSeq: record.lastSeq })
  }

  /** Recovery has started. Frames for this channel are dropped until it ends —
   *  they describe a stream the client is in the middle of replacing. */
  beginRecovery(channel: ChannelName): void {
    const record = this.#channels.get(channel) ?? this.#track(channel)
    record.expectedSeq = null
    record.lastSeq = null
    this.#setState(record, 'RECOVERING')
  }

  /**
   * Recovery could not be completed and is being given up on.
   *
   * **`STALE`, never `SYNCED`** — that is the whole difference from
   * `endRecovery`, and it is the difference between a pill that stays up over a
   * book nobody is repairing and one that comes down over it. The socket was
   * closed before the resubscribe, or the `ack` never landed; either way this
   * client has no evidence the stream is whole.
   *
   * The `seq` expectation is voided the way `endRecovery` voids it, so the next
   * frame that does arrive re-anchors rather than being read as a second gap —
   * a channel that cannot recover must not also become a recovery loop. What it
   * does *not* do is claim the channel is synced, so `staleChannels` keeps it
   * and the order-entry gate keeps blocking (`domain/order-validation.ts`).
   */
  abandonRecovery(channel: ChannelName): void {
    const record = this.#channels.get(channel) ?? this.#track(channel)
    record.expectedSeq = null
    record.lastSeq = null
    this.#setState(record, 'STALE')
  }

  /** Recovery finished without an `ack` to anchor on — a REST snapshot alone.
   *  The next frame is accepted whatever its `seq`, and every frame after it
   *  must follow by one. */
  endRecovery(channel: ChannelName): void {
    const record = this.#channels.get(channel) ?? this.#track(channel)
    record.expectedSeq = null
    this.#setState(record, 'SYNCED')
  }

  /** A reconnect voids every `seq`: there is no session resumption and no `seq`
   *  continuation across sockets, so contiguity across one is not a thing that
   *  can be observed (asyncapi §5.8). */
  reset(): void {
    for (const record of this.#channels.values()) {
      record.lastSeq = null
      record.expectedSeq = null
      this.#setState(record, 'PENDING')
    }
  }

  forget(channel: ChannelName): void {
    this.#channels.delete(channel)
  }

  #track(channel: ChannelName): ChannelSequence {
    const record: ChannelSequence = {
      channel,
      state: 'PENDING',
      lastSeq: null,
      expectedSeq: null,
      gaps: 0,
      checksumMismatches: 0,
      duplicates: 0,
    }
    this.#channels.set(channel, record)
    return record
  }

  #setState(record: ChannelSequence, state: ChannelSyncState): void {
    if (record.state === state) return
    record.state = state
    this.#emitState(record, state)
  }

  #emitState(record: ChannelSequence, state: ChannelSyncState): void {
    this.#handlers.onStateChange?.(record.channel, state)
  }
}
